import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { LiveAPIProvider } from "./core/live-api/LiveAPIContext";
import { LiveClientOptions, Job } from "./types";
import { useSession } from "./contexts/SessionContext";
import { useAuth } from "./contexts/AuthContext";
import { suggestJobs } from "./services/api";
import { useMemory } from "./core/memory/use-memory";
import MainContent from "./components/MainContent";
import { CircularProgress, Typography } from "@mui/material";
import DefaultLayout from "./layouts/DefaultLayout";
import { Alert } from "@mui/material";
import {
  JobMatchingStatusDialog,
  JobMatchingStatus,
} from "./components/JobMatchingStatusDialog";
import {
  DEFAULT_LANGUAGE,
  getDefaultLanguage,
  getAvailableLanguages,
} from "./config";
import { t } from "./translations";
import { has } from "lodash";

const AppInitializer: React.FC = () => {
  const { sessionId, isSessionActive, createSession } = useSession();
  const { user, loading } = useAuth();
  const [countryCode, setCountryCode] = useState<string>("");

  // get language based on country code
  const language = useMemo(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage");
    if (countryCode) {
      const availableLanguages = getAvailableLanguages(countryCode);
      const defaultLanguage = getDefaultLanguage(countryCode);
      if (savedLanguage && availableLanguages.includes(savedLanguage)) {
        return savedLanguage;
      }
      return defaultLanguage;
    }
    return savedLanguage || DEFAULT_LANGUAGE;
  }, [countryCode]);

  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [suggestedJobs, setSuggestedJobs] = useState<Job[]>([]);
  const [restrictionAnalysis, setRestrictionAnalysis] = useState<
    Record<string, { count: number; percentage: number }>
  >({});
  const [isShowingJobs, setIsShowingJobs] = useState(false);
  const { memoryKv, latestProfileEntry, handleNoteMade, clearMemory } =
    useMemory();

  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);

  const [jobMatchingStatus, setJobMatchingStatus] =
    useState<JobMatchingStatus>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasTimedOutRef = useRef(false);
  const timersRef = useRef<{
    oneMinute?: NodeJS.Timeout;
    twoMinutes?: NodeJS.Timeout;
  }>({});
  const requestCounterRef = useRef(0);

  const clearAllTimers = useCallback(() => {
    if (timersRef.current.oneMinute) clearTimeout(timersRef.current.oneMinute);
    if (timersRef.current.twoMinutes)
      clearTimeout(timersRef.current.twoMinutes);
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      if (loading) {
        return;
      }

      const userId = user?.talent_id;

      if (!user || !userId) {
        setIsLoadingUser(false);
        return;
      }

      try {
        setIsLoadingUser(true);
        setUserError(null);

        const country = user?.country;
        if (!country) {
          throw new Error(
            "Country is not set in the user's profile. Please specify a country (e.g., 'Poland' or 'Belgium').",
          );
        }

        const countryUpper = country.toUpperCase();
        let resolvedCountryCode: string;

        if (countryUpper === "POLAND") {
          resolvedCountryCode = "PL";
        } else if (countryUpper === "BELGIUM") {
          resolvedCountryCode = "BE";
        } else if (countryUpper === "DEMO") {
          resolvedCountryCode = "DEMO";
        } else {
          throw new Error(
            `Unsupported country: "${country}". Supported countries are "Poland", "Belgium", and "Demo".`,
          );
        }
        setCountryCode(resolvedCountryCode);

        if (
          !sessionId &&
          !isSessionActive &&
          !sessionStorage.getItem("activeSessionId")
        ) {
          console.log("Creating new session for user:", userId);
          clearMemory();
          await createSession(userId);
        } else {
          console.log(
            "Session already exists or being restored, skipping creation",
          );
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
        setUserError(
          `Failed to load your profile data. Please try refreshing the page.`,
        );
      } finally {
        setIsLoadingUser(false);
      }
    };

    loadUserData();
  }, [user, loading, sessionId, isSessionActive, createSession, clearMemory]);

  useEffect(() => {
    return () => {
      clearAllTimers();
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [clearAllTimers]);

  const handleJobSuggestions = useCallback(async () => {
    const requestId = ++requestCounterRef.current;

    // Abort previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear any existing timers from previous requests
    clearAllTimers();

    setIsLoadingJobs(true);
    setRestrictionAnalysis({});
    setJobMatchingStatus("loading");
    hasTimedOutRef.current = false;

    if (!sessionId) {
      console.error("Cannot fetch jobs: No active session");
      setIsLoadingJobs(false);
      setJobMatchingStatus(null);
      return [];
    }

    // Create abort controller for this request and set up timers for status updates
    abortControllerRef.current = new AbortController();

    timersRef.current.oneMinute = setTimeout(() => {
      setJobMatchingStatus("delayed");
    }, 60000);

    timersRef.current.twoMinutes = setTimeout(() => {
      setJobMatchingStatus("timeout");
      hasTimedOutRef.current = true;
      abortControllerRef.current?.abort();
      setIsLoadingJobs(false);
    }, 120000);

    try {
      const { jobs, analysis } = await suggestJobs(sessionId);

      // Only process results if this is the latest request
      if (requestId === requestCounterRef.current) {
        clearAllTimers();

        setSuggestedJobs(jobs);
        setRestrictionAnalysis(analysis);
        setIsShowingJobs(true);
        setJobMatchingStatus(null);
        console.log(`Successfully loaded ${jobs.length} job suggestions`);
        return jobs;
      } else {
        console.log("Stale job suggestion response ignored");
        return [];
      }
    } catch (error: any) {
      // Only handle errors if this is the latest request
      if (requestId === requestCounterRef.current) {
        console.error("Error fetching job suggestions:", error);

        clearAllTimers();

        if (error.name === "AbortError" || error.name === "CanceledError") {
          console.log("Request was cancelled due to timeout");
        } else {
          setJobMatchingStatus(null);
        }
      }
      return [];
    } finally {
      if (requestId === requestCounterRef.current && !hasTimedOutRef.current) {
        setIsLoadingJobs(false);
      }
    }
  }, [sessionId, clearAllTimers]);

  const apiOptions: LiveClientOptions = useMemo(() => {
    return {
      apiKey: "",
      httpOptions: { apiVersion: "v1alpha" },
      onJobSuggestions: handleJobSuggestions,
      onNoteMade: handleNoteMade,
    };
  }, [handleJobSuggestions, handleNoteMade]);

  if (loading || isLoadingUser) {
    const loadingStyles = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
      gap: 2,
      textAlign: "center",
    } as const;
    return (
      <DefaultLayout>
        <div style={loadingStyles}>
          <CircularProgress size={48} />
          <Typography variant="h6">
            {t("loadingUserProfile", language)}
          </Typography>
          {user && (
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          )}
        </div>
      </DefaultLayout>
    );
  }

  if (userError) {
    const errorStyles = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
      gap: 2,
      maxWidth: 600,
      margin: "auto",
      textAlign: "center",
    } as const;
    return (
      <DefaultLayout>
        <div style={errorStyles}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              {t("userLoadingError", language)}
            </Typography>
            <Typography variant="body2">
              {t("userLoadingErrorMessage", language)}
            </Typography>
          </Alert>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <LiveAPIProvider options={apiOptions}>
      <JobMatchingStatusDialog
        status={jobMatchingStatus}
        onDismiss={() => setJobMatchingStatus(null)}
        language={language}
      />
      <MainContent
        isLoadingJobs={isLoadingJobs}
        suggestedJobs={suggestedJobs}
        isShowingJobs={isShowingJobs}
        setIsShowingJobs={setIsShowingJobs}
        sessionId={sessionId}
        memoryKv={memoryKv}
        latestProfileEntry={latestProfileEntry}
        userId={user?.talent_id}
        countryCode={countryCode}
        restrictionAnalysis={restrictionAnalysis}
      />
    </LiveAPIProvider>
  );
};

export default AppInitializer;
